import React, { useState, useMemo } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    ChevronUp,
    ChevronDown,
    Loader2,
} from 'lucide-react';
import { Checkbox } from 'ui/shadcn/checkbox';
import { cn } from 'lib/utils';

export interface Column<T = any> {
    key: string;
    header: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    sortable?: boolean;
    render?: (value: any, row: T, index: number) => React.ReactNode;
    className?: string;
}

export interface TableProps<T = any> {
    columns: Column<T>[];
    data: T[];
    className?: string;

    // Pagination
    pagination?: boolean;
    pageSize?: number;
    currentPage?: number;
    onPageChange?: (page: number) => void;

    // Selection
    selectable?: boolean;
    selectedRows?: T[];
    onSelectionChange?: (selectedRows: T[]) => void;

    // Sorting
    defaultSortKey?: string;
    defaultSortOrder?: 'asc' | 'desc';
    onSort?: (key: string, order: 'asc' | 'desc') => void;

    // Actions
    actions?: (row: T) => React.ReactNode;
    actionsColumnWidth?: string;

    // Empty state
    emptyMessage?: string;

    // Row click
    onRowClick?: (row: T, index: number) => void;
    rowClassName?: (row: T, index: number) => string;

    // Loading
    loading?: boolean;

    // Fixed header
    stickyHeader?: boolean;
    maxHeight?: string;
}

const Table = <T extends Record<string, any>>({
    columns,
    data = [],
    className = '',
    pagination = false,
    pageSize = 10,
    currentPage = 1,
    onPageChange,
    selectable = false,
    selectedRows = [],
    onSelectionChange,
    defaultSortKey = '',
    defaultSortOrder = 'asc',
    onSort,
    actions,
    actionsColumnWidth = '120px',
    emptyMessage = '데이터가 없습니다.',
    onRowClick,
    rowClassName,
    loading = false,
    stickyHeader = false,
    maxHeight,
}: TableProps<T>) => {
    const [sortKey, setSortKey] = useState(defaultSortKey);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);
    const [internalCurrentPage, setInternalCurrentPage] = useState(currentPage);

    const sortedData = useMemo(() => {
        if (!sortKey) return data;
        return [...data].sort((a, b) => {
            const aValue = a[sortKey];
            const bValue = b[sortKey];
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;
            let comparison = 0;
            if (aValue > bValue) comparison = 1;
            if (aValue < bValue) comparison = -1;
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    }, [data, sortKey, sortOrder]);

    const paginatedData = useMemo(() => {
        if (!pagination) return sortedData;
        const startIndex = (internalCurrentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, pagination, internalCurrentPage, pageSize]);

    const totalPages = Math.ceil(data.length / pageSize);

    const handleSort = (key: string) => {
        const newOrder = sortKey === key && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortKey(key);
        setSortOrder(newOrder);
        onSort?.(key, newOrder);
    };

    const handlePageChange = (page: number) => {
        setInternalCurrentPage(page);
        onPageChange?.(page);
    };

    const allRowsSelected =
        selectedRows.length === paginatedData.length && paginatedData.length > 0;

    const handleSelectAll = () => {
        if (!onSelectionChange) return;
        if (allRowsSelected) {
            onSelectionChange([]);
        } else {
            onSelectionChange(paginatedData);
        }
    };

    const isRowSelected = (row: T) =>
        selectedRows.some((r) => JSON.stringify(r) === JSON.stringify(row));

    const handleSelectRow = (row: T) => {
        if (!onSelectionChange) return;
        if (isRowSelected(row)) {
            onSelectionChange(
                selectedRows.filter((r) => JSON.stringify(r) !== JSON.stringify(row))
            );
        } else {
            onSelectionChange([...selectedRows, row]);
        }
    };

    const totalCols = columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0);

    const renderPagination = () => {
        if (!pagination || totalPages <= 1) return null;
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, internalCurrentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        for (let i = startPage; i <= endPage; i++) pages.push(i);

        const startIdx = (internalCurrentPage - 1) * pageSize + 1;
        const endIdx = Math.min(internalCurrentPage * pageSize, data.length);

        return (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border bg-neutral-50">
                <div className="text-xs text-muted-foreground tabular-nums">
                    <span className="font-medium text-foreground">
                        {startIdx}–{endIdx}
                    </span>{' '}
                    / 전체{' '}
                    <span className="font-medium text-foreground">{data.length}</span>건
                </div>
                <div className="flex items-center gap-1">
                    <PageButton
                        disabled={internalCurrentPage === 1}
                        onClick={() => handlePageChange(1)}
                        aria-label="처음"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </PageButton>
                    <PageButton
                        disabled={internalCurrentPage === 1}
                        onClick={() => handlePageChange(internalCurrentPage - 1)}
                        aria-label="이전"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </PageButton>
                    {pages.map((page) => (
                        <PageButton
                            key={page}
                            active={page === internalCurrentPage}
                            onClick={() => handlePageChange(page)}
                        >
                            <span className="tabular-nums">{page}</span>
                        </PageButton>
                    ))}
                    <PageButton
                        disabled={internalCurrentPage === totalPages}
                        onClick={() => handlePageChange(internalCurrentPage + 1)}
                        aria-label="다음"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </PageButton>
                    <PageButton
                        disabled={internalCurrentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                        aria-label="마지막"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </PageButton>
                </div>
            </div>
        );
    };

    const tableEl = (
        <table className={cn('w-full caption-bottom text-base border-collapse', className)}>
            <thead
                className={cn(
                    'bg-neutral-50',
                    stickyHeader && 'sticky top-0 z-10'
                )}
            >
                <tr className="border-b border-border">
                    {selectable && (
                        <th className="w-10 px-3 h-9 text-left align-middle">
                            <Checkbox
                                checked={allRowsSelected}
                                onCheckedChange={handleSelectAll}
                                aria-label="전체 선택"
                            />
                        </th>
                    )}
                    {columns.map((column) => {
                        const isSorted = sortKey === column.key;
                        return (
                            <th
                                key={column.key}
                                className={cn(
                                    'h-9 px-3 align-middle text-sm font-semibold text-neutral-700 whitespace-nowrap select-none',
                                    column.sortable && 'cursor-pointer hover:text-foreground',
                                    column.className
                                )}
                                style={{
                                    width: column.width,
                                    minWidth: column.width || '100px',
                                    textAlign: column.align || 'left',
                                }}
                                onClick={() => column.sortable && handleSort(column.key)}
                            >
                                <div
                                    className={cn(
                                        'inline-flex items-center gap-1',
                                        column.align === 'right' && 'justify-end w-full',
                                        column.align === 'center' && 'justify-center w-full'
                                    )}
                                >
                                    <span>{column.header}</span>
                                    {column.sortable && (
                                        <span
                                            className={cn(
                                                'inline-flex flex-col text-[10px] leading-none',
                                                isSorted ? 'text-primary' : 'text-neutral-300'
                                            )}
                                        >
                                            {isSorted && sortOrder === 'asc' ? (
                                                <ChevronUp className="h-3 w-3" />
                                            ) : isSorted && sortOrder === 'desc' ? (
                                                <ChevronDown className="h-3 w-3" />
                                            ) : (
                                                <ChevronDown className="h-3 w-3 opacity-50" />
                                            )}
                                        </span>
                                    )}
                                </div>
                            </th>
                        );
                    })}
                    {actions && (
                        <th
                            className="h-9 px-3 text-center align-middle text-sm font-semibold text-neutral-700 whitespace-nowrap"
                            style={{ width: actionsColumnWidth }}
                        >
                            작업
                        </th>
                    )}
                </tr>
            </thead>
            <tbody>
                {loading ? (
                    <tr>
                        <td
                            colSpan={totalCols}
                            className="px-3 py-12 text-center text-muted-foreground"
                        >
                            <div className="flex flex-col items-center gap-2">
                                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                <span className="text-sm">데이터를 불러오는 중...</span>
                            </div>
                        </td>
                    </tr>
                ) : paginatedData.length === 0 ? (
                    <tr>
                        <td
                            colSpan={totalCols}
                            className="px-3 py-12 text-center text-sm text-muted-foreground"
                        >
                            {emptyMessage}
                        </td>
                    </tr>
                ) : (
                    paginatedData.map((row, rowIndex) => {
                        const selected = isRowSelected(row);
                        return (
                            <tr
                                key={rowIndex}
                                onClick={() => onRowClick?.(row, rowIndex)}
                                data-state={selected ? 'selected' : undefined}
                                className={cn(
                                    'border-b border-border transition-colors duration-fast',
                                    'hover:bg-navy-50 data-[state=selected]:bg-navy-100',
                                    onRowClick && 'cursor-pointer',
                                    rowClassName?.(row, rowIndex)
                                )}
                            >
                                {selectable && (
                                    <td
                                        className="w-10 px-3 py-2 align-middle"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Checkbox
                                            checked={selected}
                                            onCheckedChange={() => handleSelectRow(row)}
                                            aria-label="행 선택"
                                        />
                                    </td>
                                )}
                                {columns.map((column) => (
                                    <td
                                        key={column.key}
                                        className={cn(
                                            'px-3 py-2 align-middle text-base text-foreground',
                                            column.className
                                        )}
                                        style={{ textAlign: column.align || 'left' }}
                                    >
                                        {column.render
                                            ? column.render(row[column.key], row, rowIndex)
                                            : row[column.key]}
                                    </td>
                                ))}
                                {actions && (
                                    <td
                                        className="px-3 py-2 text-center align-middle"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="inline-flex items-center justify-center gap-1">
                                            {actions(row)}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        );
                    })
                )}
            </tbody>
        </table>
    );

    return (
        <div className="w-full">
            <div
                className="overflow-auto"
                style={maxHeight ? { maxHeight } : undefined}
            >
                {tableEl}
            </div>
            {renderPagination()}
        </div>
    );
};

const PageButton: React.FC<
    React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }
> = ({ active, className, children, ...props }) => (
    <button
        type="button"
        className={cn(
            'inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded text-xs transition-colors duration-fast',
            'border border-transparent',
            active
                ? 'bg-primary text-primary-foreground'
                : 'text-neutral-700 hover:bg-muted disabled:text-neutral-400 disabled:hover:bg-transparent',
            'disabled:cursor-not-allowed',
            className
        )}
        {...props}
    >
        {children}
    </button>
);

export default Table;
